import { API_BASE_URL } from "../config"

// Servicio PÚBLICO (sin token) para la página temporal de corrección de sucursales.
// Usa los endpoints abiertos /api/sucursales/edicion-publica del backend real.
// El backend fuerza la restricción (empresa 4, id >= 71), aquí solo consumimos.
const BASE_URL = `${API_BASE_URL}/api/sucursales/edicion-publica`

const edicionParqueaderoService = {
  /**
   * Lista las sucursales corregibles (empresa 4, id >= 71).
   * @returns {Promise<Array>} Lista de sucursales.
   */
  async list() {
    const response = await fetch(BASE_URL)
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Error ${response.status}: ${errorText || "Sin detalles"}`)
    }
    return response.json()
  },

  /**
   * Obtiene una sucursal corregible por ID.
   * @param {number} id
   * @returns {Promise<Object>} Sucursal.
   */
  async getOne(id) {
    const response = await fetch(`${BASE_URL}/${id}`)
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Error ${response.status}: ${errorText || "Sin detalles"}`)
    }
    return response.json()
  },

  /**
   * Convierte una sucursal del backend al estado del formulario.
   * @param {Object} sucursal
   * @returns {Object} form
   */
  toForm(sucursal) {
    return {
      nombre: sucursal.nombre ?? "",
      ubicacion: sucursal.ubicacion ?? "",
      preciotarifa: sucursal.preciotarifa != null ? String(sucursal.preciotarifa) : "",
      plazas: sucursal.plazas != null ? String(sucursal.plazas) : "",
      // El backend envía la hora como "HH:mm:ss"; el input type="time" usa "HH:mm".
      horaApertura: sucursal.horaApertura ? String(sucursal.horaApertura).slice(0, 5) : "",
      horaCierre: sucursal.horaCierre ? String(sucursal.horaCierre).slice(0, 5) : "",
      descripcion: sucursal.descripcion ?? "",
      latitud: sucursal.latitud != null ? Number(sucursal.latitud) : null,
      longitud: sucursal.longitud != null ? Number(sucursal.longitud) : null,
    }
  },

  /**
   * Formatea el estado del formulario al contrato del backend (entidad Sucursal).
   * No se envían empresa ni estado: el backend los deja intactos.
   * @param {Object} form
   * @returns {Object} payload
   */
  formatData(form) {
    return {
      nombre: form.nombre,
      ubicacion: form.ubicacion,
      preciotarifa: Number.parseFloat(form.preciotarifa),
      latitud: Number.parseFloat(form.latitud),
      longitud: Number.parseFloat(form.longitud),
      plazas: Number.parseInt(form.plazas, 10),
      horaApertura: form.horaApertura ? `${form.horaApertura}:00` : null,
      horaCierre: form.horaCierre ? `${form.horaCierre}:00` : null,
      descripcion: form.descripcion?.trim() || null,
    }
  },

  /**
   * Actualiza una sucursal.
   * @param {number} id
   * @param {Object} data - payload ya formateado (ver formatData).
   * @returns {Promise<Object>} Sucursal actualizada.
   */
  async update(id, data) {
    const response = await fetch(`${BASE_URL}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Error ${response.status}: ${errorText || "Sin detalles"}`)
    }

    return response.json()
  },

  // ===== Fotografías =====

  /**
   * Lista las fotos de una sucursal editable.
   * @param {number} sucursalId
   * @returns {Promise<Array>} [{ id, idSucursal, fotografia }]
   */
  async listFotos(sucursalId) {
    const response = await fetch(`${BASE_URL}/${sucursalId}/fotografias`)
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Error ${response.status}: ${errorText || "Sin detalles"}`)
    }
    return response.json()
  },

  /**
   * Asocia una foto (URL ya subida) a la sucursal.
   * @param {number} sucursalId
   * @param {string} url
   * @returns {Promise<Object>} foto creada { id, idSucursal, fotografia }
   */
  async addFoto(sucursalId, url) {
    const response = await fetch(`${BASE_URL}/${sucursalId}/fotografias`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fotografia: url }),
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Error ${response.status}: ${errorText || "Sin detalles"}`)
    }
    return response.json()
  },

  /**
   * Elimina una foto de la sucursal.
   * @param {number} sucursalId
   * @param {number} fotoId
   */
  async deleteFoto(sucursalId, fotoId) {
    const response = await fetch(`${BASE_URL}/${sucursalId}/fotografias/${fotoId}`, {
      method: "DELETE",
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Error ${response.status}: ${errorText || "Sin detalles"}`)
    }
    return { success: true }
  },
}

export default edicionParqueaderoService
